import React from 'react';
import { gql, useMutation, useQuery } from '@apollo/client';
import { AlertCircle, Search, Send } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useUser } from '@/store/useUser';
import { hasAnyPolicy } from '@/utils/policies';

const GET_NOTIFICATION_LOG_PROGRAMS = gql`
  query NotificationLogPrograms {
    notificationLogPrograms {
      id
      name
      slug
    }
  }
`;

const GET_NOTIFICATION_SEND_LOG = gql`
  query NotificationSendLog($programID: Int, $source: String, $search: String, $limit: Int, $offset: Int) {
    notificationSendLog(programID: $programID, source: $source, search: $search, limit: $limit, offset: $offset) {
      id
      source
      programID
      programSlug
      programName
      templateName
      participantID
      subject
      body
      sentAt
    }
  }
`;

const DUMMY_SEND_NOTIFICATION = gql`
  mutation DummySendNotification(
    $programID: Int
    $participantID: Int!
    $toAddress: String!
    $ccAddress: String
    $replyToAddress: String
    $subject: String!
    $body: String!
  ) {
    dummySendNotification(
      programID: $programID
      participantID: $participantID
      toAddress: $toAddress
      ccAddress: $ccAddress
      replyToAddress: $replyToAddress
      subject: $subject
      body: $body
    ) {
      id
      participantID
      subject
      sentAt
    }
  }
`;

type NotificationLogProgram = {
  id: number;
  name: string;
  slug?: string | null;
};

type NotificationSendLogEntry = {
  id: number;
  source?: string | null;
  programID?: number | null;
  programSlug?: string | null;
  programName?: string | null;
  templateName?: string | null;
  participantID?: number | null;
  subject?: string | null;
  body?: string | null;
  sentAt?: string | null;
};

type SendFormState = {
  participantID: string;
  programID: string;
  toAddress: string;
  ccAddress: string;
  replyToAddress: string;
  subject: string;
  body: string;
};

const emptySendForm = (programID?: number | null): SendFormState => ({
  participantID: '',
  programID: programID ? String(programID) : 'none',
  toAddress: '',
  ccAddress: '',
  replyToAddress: '',
  subject: '',
  body: '',
});

const formatNotificationDate = (value?: string | null) => {
  if (!value) {
    return 'N/A';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const dayDifference = Math.round((startOfToday - startOfDate) / 86400000);

  if (dayDifference === 0) {
    return 'Today';
  }
  if (dayDifference === 1) {
    return 'Yesterday';
  }

  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: date.getFullYear() === now.getFullYear() ? undefined : 'numeric',
  });
};

const readableText = (value?: string | null) => {
  return (value || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|tr|li|h[1-6])>/gi, '\n')
    .replace(/<[^>]*>/g, ' ')
    .replace(/\r/g, '')
    .replace(/[ \t]+/g, ' ')
    .replace(/\n\s+/g, '\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const compactText = (value?: string | null, maxLength = 140) => {
  const text = readableText(value).replace(/\s+/g, ' ').trim();
  if (text.length <= maxLength) {
    return text || 'N/A';
  }
  return `${text.slice(0, maxLength - 3)}...`;
};

const sourceLabel = (value?: string | null) => {
  if (value === 'nova_dummy_send') {
    return 'Nova Send';
  }
  if (value === 'notification_template') {
    return 'Template';
  }
  if (value === 'secure_mail') {
    return 'Secure Mail';
  }
  if (value === 'public_referral') {
    return 'Public Referral';
  }
  return value || 'System';
};

const notificationTitle = (entry: NotificationSendLogEntry) => {
  return readableText(entry.subject) || entry.templateName || sourceLabel(entry.source);
};

const notificationType = (entry: NotificationSendLogEntry) => {
  return entry.templateName || sourceLabel(entry.source);
};

export const NotificationLogTool: React.FC = () => {
  const { user } = useUser();
  const canSendNotification = hasAnyPolicy(user, ['tools:notifications~full', 'tools:notifications~full', 'tools:notifications~full']);
  const [search, setSearch] = React.useState('');
  const [programID, setProgramID] = React.useState<number | null>(null);
  const [source, setSource] = React.useState('all');
  const [selectedID, setSelectedID] = React.useState<number | null>(null);
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendError, setSendError] = React.useState<string | null>(null);
  const [sendForm, setSendForm] = React.useState<SendFormState>(() => emptySendForm());

  const { data: programsData } = useQuery(GET_NOTIFICATION_LOG_PROGRAMS, {
    fetchPolicy: 'cache-first'
  });

  const queryVariables = React.useMemo(
    () => ({
      programID: programID || undefined,
      source: source === 'all' ? undefined : source,
      search: search.trim() || undefined,
      limit: 200,
      offset: 0
    }),
    [programID, search, source]
  );

  const { data, loading, error, refetch } = useQuery(GET_NOTIFICATION_SEND_LOG, {
    variables: queryVariables,
    fetchPolicy: 'cache-and-network'
  });
  const [dummySendNotification, { loading: sending }] = useMutation(DUMMY_SEND_NOTIFICATION);

  const programs: NotificationLogProgram[] = programsData?.notificationLogPrograms || [];
  const entries: NotificationSendLogEntry[] = data?.notificationSendLog || [];
  const selectedEntry = React.useMemo(
    () => entries.find((entry) => entry.id === selectedID) || entries[0] || null,
    [entries, selectedID]
  );

  React.useEffect(() => {
    if (entries.length === 0) {
      setSelectedID(null);
      return;
    }
    if (!selectedID || !entries.some((entry) => entry.id === selectedID)) {
      setSelectedID(entries[0].id);
    }
  }, [entries, selectedID]);

  const updateSendForm = (field: keyof SendFormState, value: string) => {
    setSendForm((current) => ({ ...current, [field]: value }));
  };
  const openSendDialog = () => {
    setSendError(null);
    setSendForm(emptySendForm(programID));
    setSendOpen(true);
  };
  const submitDummySend = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSendError(null);

    const participantID = Number(sendForm.participantID);
    const toAddress = sendForm.toAddress.trim();
    const subject = sendForm.subject.trim();
    const body = sendForm.body.trim();

    if (!Number.isInteger(participantID) || participantID <= 0) {
      setSendError('Participant ID is required.');
      return;
    }
    if (!toAddress || !subject || !body) {
      setSendError('To, subject, and message are required.');
      return;
    }

    try {
      await dummySendNotification({
        variables: {
          programID: sendForm.programID === 'none' ? undefined : Number(sendForm.programID),
          participantID,
          toAddress,
          ccAddress: sendForm.ccAddress.trim() || undefined,
          replyToAddress: sendForm.replyToAddress.trim() || undefined,
          subject,
          body,
        },
      });

      setSearch('');
      setProgramID(null);
      setSource('all');
      await refetch({
        programID: undefined,
        source: undefined,
        search: undefined,
        limit: 200,
        offset: 0,
      });
      setSendOpen(false);
      setSendForm(emptySendForm());
    } catch (sendFailure: any) {
      setSendError(sendFailure?.message || 'Unable to send notification.');
    }
  };

  return (
    <div className="min-h-screen space-y-4 p-6">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Notifications</h1>
          <p className="text-sm text-gray-500">Search and read notification messages</p>
        </div>
        {canSendNotification ? (
          <Button onClick={openSendDialog} className="h-9 gap-2">
            <Send className="h-4 w-4" />
            Send
          </Button>
        ) : null}
      </div>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent contentWidth="720px" contentHeight="auto" contentMaxWidth="calc(100vw - 32px)">
          <DialogHeader>
            <DialogTitle>Send Notification</DialogTitle>
          </DialogHeader>
          <form className="space-y-4" onSubmit={submitDummySend}>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notification-participant-id">Participant ID</Label>
                <Input
                  id="notification-participant-id"
                  value={sendForm.participantID}
                  onChange={(event) => updateSendForm('participantID', event.target.value)}
                  inputMode="numeric"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification-program">Program</Label>
                <Select
                  value={sendForm.programID}
                  onValueChange={(value) => updateSendForm('programID', value)}
                >
                  <SelectTrigger id="notification-program" className="h-9">
                    <SelectValue placeholder="No program" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No Program</SelectItem>
                    {programs.map((program) => (
                      <SelectItem key={program.id} value={String(program.id)}>
                        {program.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-to">To</Label>
              <Input
                id="notification-to"
                value={sendForm.toAddress}
                onChange={(event) => updateSendForm('toAddress', event.target.value)}
                placeholder="name@example.org"
                className="h-9"
              />
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="notification-cc">CC</Label>
                <Input
                  id="notification-cc"
                  value={sendForm.ccAddress}
                  onChange={(event) => updateSendForm('ccAddress', event.target.value)}
                  placeholder="Optional"
                  className="h-9"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="notification-reply-to">Reply To</Label>
                <Input
                  id="notification-reply-to"
                  value={sendForm.replyToAddress}
                  onChange={(event) => updateSendForm('replyToAddress', event.target.value)}
                  placeholder="Optional"
                  className="h-9"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-subject">Subject</Label>
              <Input
                id="notification-subject"
                value={sendForm.subject}
                onChange={(event) => updateSendForm('subject', event.target.value)}
                className="h-9"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notification-body">Message</Label>
              <Textarea
                id="notification-body"
                value={sendForm.body}
                onChange={(event) => updateSendForm('body', event.target.value)}
                className="min-h-[140px]"
              />
            </div>

            {sendError ? (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{sendError}</AlertDescription>
              </Alert>
            ) : null}

            <DialogFooter className="flex-row justify-end">
              <Button type="submit" disabled={sending}>
                {sending ? 'Sending...' : 'Send Notification'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setSendOpen(false)} disabled={sending}>
                Cancel
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {error ? (
        <Alert variant="destructive">
          <AlertDescription>{error.message}</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader className="gap-3">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <CardTitle className="text-base">Notification Search</CardTitle>
            <div className="flex flex-col gap-2 md:flex-row md:items-center">
              <div className="relative md:w-80">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search notifications"
                  className="h-9 pl-8"
                />
              </div>
              <Select
                value={programID ? String(programID) : 'all'}
                onValueChange={(value) => setProgramID(value === 'all' ? null : Number(value))}
              >
                <SelectTrigger className="h-9 md:w-[220px]">
                  <SelectValue placeholder="All programs" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Programs</SelectItem>
                  {programs.map((program) => (
                    <SelectItem key={program.id} value={String(program.id)}>
                      {program.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={source} onValueChange={setSource}>
                <SelectTrigger className="h-9 md:w-[165px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Types</SelectItem>
                  <SelectItem value="nova_dummy_send">Nova Sends</SelectItem>
                  <SelectItem value="notification_template">Templates</SelectItem>
                  <SelectItem value="secure_mail">Secure Mail</SelectItem>
                  <SelectItem value="public_referral">Public Referrals</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-hidden rounded-md border border-gray-200">
            <div className="border-b border-gray-200 bg-gray-50 px-3 py-2 text-xs font-medium uppercase text-gray-500">
              {entries.length} notification{entries.length === 1 ? '' : 's'}
            </div>
            <div className="divide-y divide-gray-100">
              {loading && entries.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">Loading notifications...</div>
              ) : null}
              {!loading && entries.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-gray-500">No notifications found.</div>
              ) : null}
              {entries.map((entry) => {
                const expanded = selectedEntry?.id === entry.id;
                return (
                  <article key={entry.id} className={expanded ? 'bg-blue-50/40' : 'bg-white'}>
                    <button
                      type="button"
                      onClick={() => setSelectedID(entry.id)}
                      aria-expanded={expanded}
                      className="block w-full px-4 py-3 text-left hover:bg-gray-50"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-semibold text-gray-900">
                            {notificationTitle(entry)}
                          </div>
                          {!expanded ? (
                            <div className="mt-1 text-xs leading-5 text-gray-500">
                              {compactText(entry.body, 180)}
                            </div>
                          ) : null}
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className="border-blue-100 bg-blue-50 text-blue-700">
                              {notificationType(entry)}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {entry.programName || entry.programSlug || 'Program N/A'}
                            </span>
                            {entry.participantID ? (
                              <span className="text-xs text-gray-500">Client #{entry.participantID}</span>
                            ) : null}
                          </div>
                        </div>
                        <div className="shrink-0 whitespace-nowrap text-xs text-gray-500">
                          {formatNotificationDate(entry.sentAt)}
                        </div>
                      </div>
                    </button>
                    {expanded ? (
                      <div className="px-4 pb-4 pt-1">
                        <div className="whitespace-pre-wrap break-words border-l-2 border-blue-200 pl-4 text-sm leading-6 text-gray-800">
                          {readableText(entry.body) || 'No message body was recorded for this notification.'}
                        </div>
                      </div>
                    ) : null}
                  </article>
                );
              })}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationLogTool;
